import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiGet, apiPost, apiPut } from '../api/http';
import { useToast } from '../components/ToastContext';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { FaGripVertical } from 'react-icons/fa';
import ProjectBoardView from './ProjectBoardView';
import './LayoutEditor.css';

export default function LayoutEditor() {
  const { mode, id } = useParams();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [version, setVersion] = useState(1);
  const [entries, setEntries] = useState([]);
  const [isActive, setIsActive] = useState(true);
  const [loading, setLoading] = useState(false);
  const [activeButton, setActiveButton] = useState(null);
  const { showToast } = useToast();

  const tableScrollRef = useRef(null);

  // -----------------------------
  // Helpers
  // -----------------------------

  // 🔹 Unique field_key generator
  const generateFieldKey = () => {
    return `field_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  };

  const renumberSteps = (list) => {
    let counter = 1;
    return list.map((e) =>
      e.type === 'STEP' ? { ...e, stepNo: counter++ } : e
    );
  };

  const getNextRow = () => {
    if (entries.length === 0) return 1;
    const maxRow = Math.max(...entries.map((e) => e.row || 1));
    return maxRow + 1;
  };

  const getNextColForRow = (row) => {
    const rowEntries = entries.filter((e) => e.row === row);
    if (rowEntries.length === 0) return 1;
    const maxCol = Math.max(...rowEntries.map((e) => e.col || 1));
    return maxCol + 1;
  };

  const resolveConflicts = (list) => {
    const occupied = new Map();
    const fixed = [];

    for (const e of list) {
      let row = e.row || 1;
      let col = e.col || 1;
      let key = `${row}-${col}`;

      while (occupied.has(key)) {
        col++;
        if (col > 10) {
          col = 1;
          row++;
        }
        key = `${row}-${col}`;
      }

      occupied.set(key, true);
      fixed.push({ ...e, row, col });
    }

    return fixed;
  };

  // -----------------------------
  // Lifecycle
  // -----------------------------

  useEffect(() => {
    if (mode === 'edit' || mode === 'copy') {
      (async () => {
        try {
          const layout = await apiGet(`/api/layouts/${id}`);
          setName(mode === 'copy' ? layout.name + ' Copy' : layout.name);
          setVersion(mode === 'copy' ? layout.version + 1 : layout.version);
          setEntries(JSON.parse(layout.json));
          setIsActive(layout.is_active === 1 || layout.is_active === true);
        } catch (err) {
          showToast(`Failed to load layout: ${err.message}`, 'error');
        }
      })();
    }
  }, [mode, id]);

  useEffect(() => {
    if (tableScrollRef.current) {
      requestAnimationFrame(() => {
        tableScrollRef.current.scrollTop = tableScrollRef.current.scrollHeight;
      });
    }
  }, [entries]);

  // -----------------------------
  // Actions
  // -----------------------------

  const handleAddEntry = (kind) => {
    setActiveButton(kind);

    let nextRow, nextCol;

    if (entries.length === 0) {
      nextRow = 1;
      nextCol = 1;
    } else {
      nextRow = getNextRow();
      nextCol = 1;
    }

    let newEntry;
    if (kind === 'INFO') {
      newEntry = {
        type: 'INFO',
        field_key: generateFieldKey(),
        name: 'New Info',
        value: '',
        row: nextRow,
        col: nextCol,
        isBold: false,
      };
    } else if (kind === 'STEP') {
      newEntry = {
        type: 'STEP',
        field_key: generateFieldKey(),
        stepNo: entries.filter((e) => e.type === 'STEP').length + 1,
        name: 'New Step',
        value: 'No',
        hasQueries: true,
        queries: 'No',
        row: nextRow,
        col: nextCol,
        rowQ: nextRow,
        colQ: nextCol + 2,
        isBold: false,
      };
    } else if (kind === 'HEADER') {
      newEntry = {
        type: 'HEADER',
        field_key: generateFieldKey(),
        name: 'New Header',
        row: nextRow,
        col: nextCol,
        isBold: true,
      };
    } else if (kind === 'DATE') {
      newEntry = {
        type: 'DATE',
        field_key: generateFieldKey(),
        name: 'New Date',
        value: null,
        row: nextRow,
        col: nextCol,
        isBold: false,
      };
    } else if (kind === 'FORMULA') {
      newEntry = {
        type: 'FORMULA',
        field_key: generateFieldKey(),
        name: 'New Formula',
        row: nextRow,
        col: nextCol,
        isBold: false,
        formula_meta: { operation: 'diff', dependencies: [] },
      };
    }


    const updated = [...entries, newEntry];
    setEntries(renumberSteps(resolveConflicts(updated)));
  };



  // 🔹 NEW: for FORMULA meta updates
  const handleUpdateFormula = (index, key, value) => {
    const updated = [...entries];
    const fm = { ...(updated[index].formula_meta || {}) };
    fm[key] = value;
    updated[index] = { ...updated[index], formula_meta: fm };
    setEntries(updated);
  };

  const normalizeEntries = (list) => renumberSteps(resolveConflicts(list));

  const handleUpdateEntry = (index, field, value) => {
    // FAST: no normalization per keystroke
    setEntries((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleDeleteEntry = (index) => {
    if (!window.confirm('Remove this entry?')) return;
    setEntries((prev) => normalizeEntries(prev.filter((_, i) => i !== index)));
  };

  const handleSave = async () => {
    if (!name || String(name).trim() === '' || !version || entries.length === 0) {
      showToast('Layout must have a name, version, and at least one entry', 'error');
      return;
    }

    const unnamed = entries.find((e) => !e.name || String(e.name).trim() === '');
    if (unnamed) {
      showToast('Each entry must have a name/label', 'error');
      return;
    }

    setLoading(true);
    try {
      // Normalize ONCE here
      const normalized = normalizeEntries(entries);
      setEntries(normalized);

      const payload = { name, version, json: normalized, is_active: isActive };

      if (mode === 'edit') {
        await apiPut(`/api/layouts/${id}`, payload);
        showToast('Layout updated successfully!', 'success');
      } else {
        await apiPost('/api/layouts', payload);
        showToast('Layout saved successfully!', 'success');
      }

      navigate('/layouts');
    } catch (err) {
      showToast(`Failed to save layout: ${err.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };










  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const reordered = Array.from(entries);
    const [removed] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, removed);
    setEntries(renumberSteps(reordered));
  };

  // -----------------------------
  // Import / Export
  // -----------------------------

  const handleExport = () => {
    try {
      const dataStr = JSON.stringify(entries, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = `${name || 'layout'}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      showToast('Layout exported successfully!', 'success');
    } catch (err) {
      showToast(`Export failed: ${err.message}`, 'error');
    }
  };

  const handleImport = (ev) => {
    const file = ev.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const json = JSON.parse(e.target.result);
        if (!Array.isArray(json)) {
          throw new Error('Invalid layout format (expected array)');
        }
        setEntries(renumberSteps(resolveConflicts(json)));
        setName(file.name.replace('.json', '') + ' Imported');
        setVersion(1);
        setIsActive(true);
        showToast('Layout imported. Ready to save as new.', 'success');
        navigate('/layout-editor/new');
      } catch (err) {
        showToast(`Import failed: ${err.message}`, 'error');
      }
    };
    reader.readAsText(file);
  };

  // -----------------------------
  // Row-band classes
  // -----------------------------
  const bandClasses = useMemo(() => {
    if (!entries || entries.length === 0) return [];

    let classes = [];
    let lastRow = null;
    let toggle = false;

    for (const e of entries) {
      if (e.row !== lastRow) {
        toggle = !toggle;
        lastRow = e.row;
      }
      classes.push(toggle ? 'row-band-odd' : 'row-band-even');
    }

    return classes;
  }, [entries]);

  // -----------------------------
  // Render
  // -----------------------------

  const stickyTh = {
    position: 'sticky',
    top: 0,
    zIndex: 2,
    backgroundColor: '#f2f2f2',
  };

  const dateFields = entries.filter((e) => e.type === 'DATE');

  return (
    <div className="card layout-editor">
      <h2>
        {mode === 'edit'
          ? 'Edit Layout'
          : mode === 'copy'
            ? 'Copy Layout'
            : 'New Layout'}
      </h2>

      {/* Top form */}
      <div className="form-row">
        <div className="form-group">
          <label>Name</label>
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </div>
        <div className="form-group small">
          <label>Version</label>
          <input
            type="number"
            value={version}
            onChange={(e) => setVersion(parseInt(e.target.value))}
            required
          />
        </div>
        <div className="form-group checkbox">
          <label>
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />{' '}
            Active
          </label>
        </div>
      </div>

      {/* Action buttons */}
      <div className="action-buttons sticky">
        <button
          className={`btn ${activeButton === 'HEADER' ? 'filled' : 'secondary'}`}
          onClick={() => handleAddEntry('HEADER')}
        >
          + Add HEADER
        </button>{' '}
        <button
          className={`btn ${activeButton === 'INFO' ? 'filled' : 'secondary'}`}
          onClick={() => handleAddEntry('INFO')}
        >
          + Add INFO
        </button>{' '}
        <button
          className={`btn ${activeButton === 'STEP' ? 'filled' : 'secondary'}`}
          onClick={() => handleAddEntry('STEP')}
        >
          + Add STEP
        </button>{' '}
        {/* 🔹 NEW buttons */}
        <button
          className={`btn ${activeButton === 'DATE' ? 'filled' : 'secondary'}`}
          onClick={() => handleAddEntry('DATE')}
        >
          + Add DATE
        </button>{' '}
        <button
          className={`btn ${activeButton === 'FORMULA' ? 'filled' : 'secondary'}`}
          onClick={() => handleAddEntry('FORMULA')}
        >
          + Add FORMULA
        </button>{' '}
        <button className="btn secondary" onClick={handleExport}>
          Export JSON
        </button>
        <label className="btn secondary" style={{ cursor: 'pointer' }}>
          Import JSON
          <input
            type="file"
            accept=".json"
            style={{ display: 'none' }}
            onChange={handleImport}
          />
        </label>
      </div>

      {/* Entries Table with Drag-and-Drop */}
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="entries">
          {(provided) => (
            <div
              ref={tableScrollRef}
              style={{ maxHeight: '60vh', overflowY: 'auto', marginTop: '1em' }}
            >
              <table className="styled-table">
                <thead>
                  <tr>
                    <th style={stickyTh}></th>
                    <th style={stickyTh}>Type</th>
                    <th style={stickyTh}>Name</th>
                    <th style={stickyTh}>Step No</th>
                    <th style={stickyTh}>Row</th>
                    <th style={stickyTh}>Col</th>
                    <th style={stickyTh}>Has Queries</th>
                    <th style={stickyTh}>RowQ</th>
                    <th style={stickyTh}>ColQ</th>
                    <th style={stickyTh}>Bold</th>
                    {/* 🔹 extra cols for FORMULA */}
                    <th style={stickyTh}>Operation</th>
                    <th style={stickyTh}>Dep 1</th>
                    <th style={stickyTh}>Dep 2</th>
                    <th style={stickyTh}>Actions</th>
                  </tr>
                </thead>
                <tbody {...provided.droppableProps} ref={provided.innerRef}>
                  {entries.map((e, idx) => (
                    <Draggable key={idx} draggableId={`entry-${idx}`} index={idx}>
                      {(provided) => (
                        <tr
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          className={`${bandClasses[idx] || ''} row-hover`}
                        >
                          <td>
                            <FaGripVertical className="drag-handle" />
                          </td>
                          <td>{e.type}</td>
                          <td>
                            <input
                              value={e.name}
                              onChange={(ev) =>
                                handleUpdateEntry(idx, 'name', ev.target.value)
                              }
                            />
                          </td>
                          <td>
                            {e.type === 'STEP' ? (
                              <input
                                type="number"
                                value={e.stepNo}
                                onChange={(ev) =>
                                  handleUpdateEntry(
                                    idx,
                                    'stepNo',
                                    parseInt(ev.target.value)
                                  )
                                }
                              />
                            ) : (
                              '-'
                            )}
                          </td>
                          <td>
                            <input
                              type="number"
                              value={e.row}
                              onChange={(ev) =>
                                handleUpdateEntry(
                                  idx,
                                  'row',
                                  parseInt(ev.target.value)
                                )
                              }
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              value={e.col}
                              onChange={(ev) =>
                                handleUpdateEntry(
                                  idx,
                                  'col',
                                  parseInt(ev.target.value)
                                )
                              }
                            />
                          </td>
                          <td>
                            {e.type === 'STEP' ? (
                              <input
                                type="checkbox"
                                checked={!!e.hasQueries}
                                onChange={(ev) =>
                                  handleUpdateEntry(
                                    idx,
                                    'hasQueries',
                                    ev.target.checked
                                  )
                                }
                              />
                            ) : (
                              '-'
                            )}
                          </td>
                          <td>
                            {e.type === 'STEP' && e.hasQueries ? (
                              <input
                                type="number"
                                value={e.rowQ}
                                onChange={(ev) =>
                                  handleUpdateEntry(
                                    idx,
                                    'rowQ',
                                    parseInt(ev.target.value)
                                  )
                                }
                              />
                            ) : (
                              '-'
                            )}
                          </td>
                          <td>
                            {e.type === 'STEP' && e.hasQueries ? (
                              <input
                                type="number"
                                value={e.colQ}
                                onChange={(ev) =>
                                  handleUpdateEntry(
                                    idx,
                                    'colQ',
                                    parseInt(ev.target.value)
                                  )
                                }
                              />
                            ) : (
                              '-'
                            )}
                          </td>
                          <td>
                            <input
                              type="checkbox"
                              checked={!!e.isBold}
                              onChange={(ev) =>
                                handleUpdateEntry(
                                  idx,
                                  'isBold',
                                  ev.target.checked
                                )
                              }
                            />
                          </td>

                          {/* 🔹 FORMULA extra cells */}
                          <td>
                            {e.type === 'FORMULA' ? (
                              <select
                                value={e.formula_meta?.operation || 'diff'}
                                onChange={(ev) =>
                                  handleUpdateFormula(idx, 'operation', ev.target.value)
                                }
                              >
                                <option value="diff">Diff (days)</option>
                              </select>
                            ) : '-'}
                          </td>
                          <td>
                            {e.type === 'FORMULA' ? (
                              <select
                                value={e.formula_meta?.dependencies?.[0] || ''}
                                onChange={(ev) => {
                                  const deps = [...(e.formula_meta?.dependencies || [])];
                                  deps[0] = ev.target.value;
                                  handleUpdateFormula(idx, 'dependencies', deps);
                                }}
                              >
                                <option value="">-- select --</option>
                                {dateFields.map((d, i) => (
                                  <option key={i} value={d.field_key || d.name}>
                                    {d.name}
                                  </option>
                                ))}
                              </select>
                            ) : '-'}
                          </td>
                          <td>
                            {e.type === 'FORMULA' ? (
                              <select
                                value={e.formula_meta?.dependencies?.[1] || ''}
                                onChange={(ev) => {
                                  const deps = [...(e.formula_meta?.dependencies || [])];
                                  deps[1] = ev.target.value;
                                  handleUpdateFormula(idx, 'dependencies', deps);
                                }}
                              >
                                <option value="">-- select --</option>
                                {dateFields.map((d, i) => (
                                  <option key={i} value={d.field_key || d.name}>
                                    {d.name}
                                  </option>
                                ))}
                              </select>
                            ) : '-'}
                          </td>

                          <td>
                            <button
                              className="btn small danger"
                              onClick={() => handleDeleteEntry(idx)}
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </tbody>
              </table>
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {/* Preview */}
      <div className="preview-section">
        <h3>Preview</h3>
        <ProjectBoardView
          project={{ client_name: name || 'Preview (Layout)', loan_type: '' }}
          entries={entries}
          comments={[]}
          isPreview={true}
        />
      </div>

      {/* Save / Cancel */}
      <div className="form-actions">
        <button disabled={loading} className="btn primary" onClick={handleSave}>
          {loading ? 'Saving…' : 'Save Layout'}
        </button>
        <button className="btn secondary" onClick={() => navigate('/layouts')}>
          Cancel
        </button>
      </div>
    </div>
  );
}
